import React, { useEffect, useMemo, useState } from "react";
import {
  FaExclamationTriangle,
  FaPeopleCarry,
  FaUserFriends,
  FaUsers,
} from "react-icons/fa";

import Reveal from "../../components/Reveal/Reveal";
import { getAllTeamMembers } from "../../services/teamServices";

import TeamCard from "./team/TeamCard";
import TeamProfileModal from "./team/TeamProfileModal";
import {
  cn,
  getCategoryStyle,
  groupMembersByCategory,
  hasMemberDetails,
} from "./team/teamUtils";

const TeamSkeleton = () => {
  return (
    <section className="relative overflow-hidden bg-slate-50 py-20 dark:bg-slate-950 lg:py-28">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-3xl text-center">
          <div className="mx-auto h-9 w-44 animate-pulse rounded-full bg-slate-200 dark:bg-slate-800" />

          <div className="mx-auto mt-6 h-12 w-2/3 animate-pulse rounded-2xl bg-slate-200 dark:bg-slate-800" />

          <div className="mx-auto mt-5 h-5 w-3/4 animate-pulse rounded-full bg-slate-200 dark:bg-slate-800" />
        </div>

        <div
          className="
            mt-14 grid justify-center gap-4
            grid-cols-[repeat(auto-fit,minmax(150px,190px))]
          "
        >
          {Array.from({ length: 12 }).map((_, index) => (
            <div
              key={index}
              className="
                h-64 w-full animate-pulse
                rounded-[1.6rem]
                bg-slate-200 dark:bg-slate-800
              "
            />
          ))}
        </div>
      </div>
    </section>
  );
};

const EmptyState = () => {
  return (
    <div
      className="
        rounded-[2rem]
        border border-dashed border-slate-300
        bg-white/70 p-10 text-center shadow-sm
        dark:border-white/10 dark:bg-white/5
      "
    >
      <div
        className="
          mx-auto mb-4 flex h-14 w-14
          items-center justify-center rounded-2xl
          bg-orange-100 text-orange-600
          dark:bg-orange-500/10 dark:text-orange-400
        "
      >
        <FaUserFriends />
      </div>

      <h3 className="font-barlow text-2xl font-bold text-slate-950 dark:text-white">
        Aucune équipe publiée
      </h3>

      <p className="mx-auto mt-3 max-w-xl text-sm leading-6 text-slate-500 dark:text-slate-300">
        Les membres du bureau, les enseignants et les bénévoles apparaîtront ici
        dès qu’ils seront ajoutés dans l’administration.
      </p>
    </div>
  );
};

const ErrorState = ({ message }) => {
  return (
    <div
      className="
        rounded-[1.5rem]
        border border-red-200 bg-red-50 p-4
        text-sm font-semibold text-red-700
        dark:border-red-500/20
        dark:bg-red-500/10
        dark:text-red-300
      "
    >
      <div className="flex items-center justify-center gap-3">
        <FaExclamationTriangle />
        {message}
      </div>
    </div>
  );
};

export default function NosEquipes() {
  const [team, setTeam] = useState([]);
  const [selectedMember, setSelectedMember] = useState(null);
  const [selectedMemberCategoryIndex, setSelectedMemberCategoryIndex] =
    useState(0);
  const [loading, setLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState("");

  useEffect(() => {
    let mounted = true;

    const fetchTeam = async () => {
      try {
        setLoading(true);
        setErrorMsg("");

        const data = await getAllTeamMembers();

        if (!mounted) return;

        setTeam(Array.isArray(data) ? data : []);
      } catch (error) {
        console.error(
          "Erreur lors de la récupération des équipes :",
          error
        );

        if (mounted) {
          setErrorMsg("Impossible de charger les membres de l’équipe.");
        }
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };

    fetchTeam();

    return () => {
      mounted = false;
    };
  }, []);

  const groupedEntries = useMemo(() => {
    return groupMembersByCategory(team);
  }, [team]);

  const totalMembers = team.length;
  const totalCategories = groupedEntries.length;

  const detailMembers = useMemo(() => {
    return team.filter(hasMemberDetails).length;
  }, [team]);

  const openMember = (member, categoryIndex) => {
    if (!hasMemberDetails(member)) return;

    setSelectedMember(member);
    setSelectedMemberCategoryIndex(categoryIndex);
  };

  if (loading) {
    return <TeamSkeleton />;
  }

  return (
    <section
      id="nosEquipes"
      className="
        relative scroll-mt-[calc(var(--site-navbar-h)+24px)]
        overflow-hidden bg-slate-50 py-20
        text-slate-950
        dark:bg-slate-950 dark:text-white
        lg:py-28
      "
    >
      {/* Décoration de fond */}
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(242,113,49,0.13),transparent_34%),radial-gradient(circle_at_bottom_right,rgba(59,130,246,0.10),transparent_32%)]" />

      <div className="absolute inset-0 bg-[linear-gradient(to_right,rgba(148,163,184,0.14)_1px,transparent_1px),linear-gradient(to_bottom,rgba(148,163,184,0.14)_1px,transparent_1px)] bg-[size:42px_42px] opacity-40 dark:opacity-20" />

      <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        {/* En-tête */}
        <Reveal className="mx-auto max-w-3xl text-center">
          <div
            className="
              inline-flex items-center gap-2
              rounded-full border border-orange-200
              bg-orange-50 px-4 py-2
              text-xs font-bold uppercase
              tracking-[0.22em] text-orange-700
              shadow-sm
              dark:border-orange-500/20
              dark:bg-orange-500/10
              dark:text-orange-300
            "
          >
            <FaPeopleCarry />
            Équipe du club
          </div>

          <h2 className="mt-6 font-barlow text-4xl font-bold tracking-tight text-slate-950 dark:text-white sm:text-5xl lg:text-6xl">
            Ceux qui font vivre{" "}
            <span className="text-orange-600 dark:text-orange-400">
              le club
            </span>
          </h2>

          <p className="mx-auto mt-5 max-w-2xl text-base leading-8 text-slate-600 dark:text-slate-300">
            Bureau, enseignants, bénévoles et référents : découvrez les
            personnes qui accompagnent les adhérents au quotidien.
          </p>
        </Reveal>

        {/* Statistiques */}
        <Reveal
          delay={100}
          className="mx-auto mt-10 grid max-w-4xl gap-4 sm:grid-cols-3"
        >
          <div
            className="
              rounded-[1.7rem]
              border border-slate-200 bg-white/80
              p-5 text-center shadow-sm backdrop-blur-xl
              transition duration-200
              hover:-translate-y-1
              hover:border-orange-300 hover:shadow-lg
              dark:border-white/10 dark:bg-white/5
              dark:hover:border-orange-500/40
            "
          >
            <FaUsers className="mx-auto mb-3 text-2xl text-orange-600 dark:text-orange-400" />

            <p className="font-barlow text-3xl font-bold text-slate-950 dark:text-white">
              {totalMembers}
            </p>

            <p className="mt-1 text-xs font-bold uppercase tracking-[0.18em] text-slate-500 dark:text-slate-300">
              Membre{totalMembers > 1 ? "s" : ""}
            </p>
          </div>

          <div
            className="
              rounded-[1.7rem]
              border border-slate-200 bg-white/80
              p-5 text-center shadow-sm backdrop-blur-xl
              transition duration-200
              hover:-translate-y-1
              hover:border-orange-300 hover:shadow-lg
              dark:border-white/10 dark:bg-white/5
              dark:hover:border-orange-500/40
            "
          >
            <FaUserFriends className="mx-auto mb-3 text-2xl text-orange-600 dark:text-orange-400" />

            <p className="font-barlow text-3xl font-bold text-slate-950 dark:text-white">
              {totalCategories}
            </p>

            <p className="mt-1 text-xs font-bold uppercase tracking-[0.18em] text-slate-500 dark:text-slate-300">
              Catégorie{totalCategories > 1 ? "s" : ""}
            </p>
          </div>

          <div
            className="
              rounded-[1.7rem]
              border border-slate-200 bg-white/80
              p-5 text-center shadow-sm backdrop-blur-xl
              transition duration-200
              hover:-translate-y-1
              hover:border-orange-300 hover:shadow-lg
              dark:border-white/10 dark:bg-white/5
              dark:hover:border-orange-500/40
            "
          >
            <FaPeopleCarry className="mx-auto mb-3 text-2xl text-orange-600 dark:text-orange-400" />

            <p className="font-barlow text-3xl font-bold text-slate-950 dark:text-white">
              {detailMembers}
            </p>

            <p className="mt-1 text-xs font-bold uppercase tracking-[0.18em] text-slate-500 dark:text-slate-300">
              Profil{detailMembers > 1 ? "s" : ""} détaillé
              {detailMembers > 1 ? "s" : ""}
            </p>
          </div>
        </Reveal>

        {/* Erreur */}
        {errorMsg && (
          <div className="mt-10">
            <ErrorState message={errorMsg} />
          </div>
        )}

        {/* État vide */}
        {!errorMsg && groupedEntries.length === 0 && (
          <div className="mt-14">
            <EmptyState />
          </div>
        )}

        {/* Catégories */}
        {!errorMsg && groupedEntries.length > 0 && (
          <div className="mt-14 space-y-10">
            {groupedEntries.map(([category, members], categoryIndex) => {
              const categoryStyle = getCategoryStyle(categoryIndex);

              return (
                <Reveal
                  key={category}
                  variant={categoryIndex % 2 === 0 ? "left" : "right"}
                  delay={categoryIndex * 90}
                >
                  <div
                    className="
                      overflow-hidden rounded-[2.5rem]
                      border border-slate-200
                      bg-white/80 shadow-xl backdrop-blur-xl
                      transition duration-300
                      hover:border-orange-300 hover:shadow-2xl
                      dark:border-white/10
                      dark:bg-slate-900/80
                      dark:hover:border-orange-500/40
                    "
                  >
                    {/* En-tête de catégorie */}
                    <div
                      className="
                        relative overflow-hidden
                        border-b border-slate-200
                        bg-slate-50 px-5 py-6
                        dark:border-white/10
                        dark:bg-slate-950/80
                        sm:px-7
                      "
                    >
                      <div
                        className={cn(
                          "pointer-events-none absolute -left-16 -top-16 h-44 w-44 rounded-full blur-3xl",
                          categoryStyle.soft
                        )}
                      />

                      <div className="relative flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                        <div className="flex items-center gap-4">
                          <span
                            className={cn(
                              "flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl text-white shadow-lg",
                              categoryStyle.bg
                            )}
                          >
                            <FaUsers />
                          </span>

                          <div>
                            <h3 className="font-barlow text-2xl font-extrabold uppercase tracking-tight text-slate-950 dark:text-white">
                              {category}
                            </h3>

                            <p className="mt-1 text-sm font-semibold text-slate-500 dark:text-slate-300">
                              {members.length} membre
                              {members.length > 1 ? "s" : ""}
                            </p>
                          </div>
                        </div>

                        <div
                          className={cn(
                            "inline-flex self-start rounded-full border px-4 py-2 text-xs font-bold uppercase tracking-[0.18em] sm:self-auto",
                            categoryStyle.border,
                            categoryStyle.soft,
                            categoryStyle.text
                          )}
                        >
                          Présentation
                        </div>
                      </div>
                    </div>

                    {/* Membres de la catégorie */}
                    <div className="p-5 sm:p-7">
                      <div
                        className="
                          grid justify-center gap-4
                          grid-cols-[repeat(auto-fit,minmax(150px,190px))]
                        "
                      >
                        {members.map((member, memberIndex) => (
                          <Reveal
                            key={
                              member.id ||
                              `${member.nom}-${member.prenom}-${memberIndex}`
                            }
                            variant="zoom"
                            delay={memberIndex * 60}
                            className="h-full w-full"
                          >
                            <TeamCard
                              member={member}
                              categoryStyle={categoryStyle}
                              indexInCategory={memberIndex}
                              onOpen={(selected) =>
                                openMember(selected, categoryIndex)
                              }
                            />
                          </Reveal>
                        ))}
                      </div>
                    </div>
                  </div>
                </Reveal>
              );
            })}
          </div>
        )}
      </div>

      {/* Modale du profil */}
      {selectedMember && (
        <TeamProfileModal
          member={selectedMember}
          categoryStyle={getCategoryStyle(
            selectedMemberCategoryIndex
          )}
          onClose={() => setSelectedMember(null)}
        />
      )}
    </section>
  );
}